import { later } from '@ember/runloop';
import { service } from '@ember/service';

import type { APIData, DSSetup, PopupData, RecordData, Service } from 'webapp-types';
import ThemeConfig from 'webapp/const/theme-config';
import {
	BillingFeature,
	BlockVisualization,
	ModelName,
	NewReportSlideFragmentLayout,
	PopupEvent,
	ReportSlideFragmentType,
} from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { TrackingEvent } from 'webapp/enums/tracking';

import type Block from 'webapp/models-custom/block';
import type ReportSlide from 'webapp/models-custom/report-slide';
import ReportSlideFragment from 'webapp/models-custom/report-slide-fragment';
import type VisualizationDataStory from 'webapp/models-custom/visualization/data-story';
import type ReportSlideFragmentRecord from 'webapp/models/report-slide-fragment';

import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';

export interface CreateFragmentResponseToGrid {
	fragment: ReportSlideFragment;
	afterFragmentCreateFn?: () => Promise<unknown>; // this we optionally do after fragment is actually saved
}

export default class ModelManagerReportSlideFragmentsService extends ModelManagerForStoreService<
	ReportSlideFragmentRecord,
	ReportSlideFragment
> {
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('constants') declare constantsService: Service.Constants;

	getRandomColor(): string {
		const hexColors = this.constantsService.boardColors
			.filter((item) => !item.isLight)
			.map((item) => item.hex);
		return hexColors[Math.floor(Math.random() * hexColors.length)]!;
	}

	getColor(name: string): string | undefined {
		const hexColors = this.constantsService.boardColors.find((item) => item.name === name)?.hex;
		return hexColors;
	}

	availableThemesForColor(color: string): number[] {
		let themes = [];

		const selectedColorData = this.constantsService.boardColors.find(
			(item) => item.hex === color,
		);

		if (selectedColorData) {
			themes = selectedColorData.themes;
		} else {
			themes = ThemeConfig.map((item) => item.id);
		}

		return themes;
	}

	getRecordsForSlide(slideId: number): ReportSlideFragmentRecord[] {
		// also unsaved!
		return this.allRecords
			.filter((item) => item.reportSlideId === slideId)
			.filter((f) => !f.isDeleted);
	}

	findForBlock(blockId: number) {
		return this.allRecords
			.filter((item) => item.referenceId === blockId)
			.find((f) => {
				return (
					[ReportSlideFragmentType.BLOCK, ReportSlideFragmentType.STORY_TEXT].includes(
						f.type,
					) && !f.isDeleted
				);
			});
	}

	findForBoard(boardId: number) {
		return this.allRecords
			.filter((item) => item.referenceId === boardId)
			.find((f) => {
				return f.type === ReportSlideFragmentType.BOARD && !f.isDeleted;
			});
	}

	createFragmentForSlide(
		reportSlideId: number,
		type: ReportSlideFragmentType,
		referenceId?: number,
		value: RecordData.ReportSlideFragment.Value = {},
	): ReportSlideFragment {
		const model = this.createNewModel({ reportSlideId, type, value: value });

		if (referenceId) {
			model.referenceId = referenceId;
		}
		return model;
	}

	addFragmentFromLayout(
		fragmentType: NewReportSlideFragmentLayout,
		reportSlide: ReportSlide,
		placeholder?: ReportSlideFragment,
	): Promise<CreateFragmentResponseToGrid> {
		let reportSlideFragmentType: ReportSlideFragmentType;
		switch (fragmentType) {
			case NewReportSlideFragmentLayout.TITLE:
				reportSlideFragmentType = ReportSlideFragmentType.TITLE;
				break;
			case NewReportSlideFragmentLayout.IMAGE:
				reportSlideFragmentType = ReportSlideFragmentType.IMAGE;
				break;
			case NewReportSlideFragmentLayout.METRIC:
			case NewReportSlideFragmentLayout.AI_PERFORMANCE_SUMMARY:
				reportSlideFragmentType = ReportSlideFragmentType.BLOCK;
				break;
			case NewReportSlideFragmentLayout.DATABOARD:
				reportSlideFragmentType = ReportSlideFragmentType.BOARD;
				break;
			case NewReportSlideFragmentLayout.TEXT:
			case NewReportSlideFragmentLayout.DATA_STORY:
			case NewReportSlideFragmentLayout.TABLE:
			case NewReportSlideFragmentLayout.DIVIDER:
			case NewReportSlideFragmentLayout.TITLE_2:
			default:
				reportSlideFragmentType = ReportSlideFragmentType.STORY_TEXT;
				break;
		}

		if (
			[
				NewReportSlideFragmentLayout.METRIC,
				NewReportSlideFragmentLayout.AI_PERFORMANCE_SUMMARY,
			].includes(fragmentType)
		) {
			return this.createBlockFragment(fragmentType, reportSlide, placeholder);
		} else if (fragmentType === NewReportSlideFragmentLayout.DATA_STORY) {
			return this.createDataStoryFragment(reportSlide, placeholder);
		} else if (fragmentType === NewReportSlideFragmentLayout.DATABOARD) {
			return this.createBoardFragment(reportSlide, placeholder);
		} else if (fragmentType === NewReportSlideFragmentLayout.TABLE) {
			return this.createTableFragment(reportSlide, placeholder);
		} else if (fragmentType === NewReportSlideFragmentLayout.DIVIDER) {
			return this.createDividerFragment(reportSlide, placeholder);
		} else {
			let fragment: ReportSlideFragment;
			if (placeholder) {
				fragment = placeholder;
				fragment.type = reportSlideFragmentType;
			} else {
				fragment = this.createFragmentForSlide(
					reportSlide.numericId!,
					reportSlideFragmentType,
				);
			}

			this.trackDatablockDrop(reportSlideFragmentType);

			if (fragmentType === NewReportSlideFragmentLayout.TITLE_2) {
				const afterFragmentCreateFn = () => {
					// delay is needed to ensure block exists before we trigger the event
					later(() => {
						this.eventManagerService.trigger(AppEvent.DATA_STORY_SET_INITIAL_STYLE, {
							fragment,
							styleFormat: 'h4',
						});
					}, 500);
					return Promise.resolve();
				};

				return Promise.resolve({ fragment, afterFragmentCreateFn });
			} else {
				return Promise.resolve({ fragment });
			}
		}
	}

	private createBlockFragment(
		layout: NewReportSlideFragmentLayout,
		reportSlide: ReportSlide,
		placeholder?: ReportSlideFragment,
	): Promise<CreateFragmentResponseToGrid> {
		return new Promise((resolve, reject) => {
			const extraOptions: Partial<PopupData.Report.CreateBlock> = {};
			if (layout === NewReportSlideFragmentLayout.AI_PERFORMANCE_SUMMARY) {
				extraOptions.blockVisualization = BlockVisualization.AI_SUMMARY;
				extraOptions.blockVisualizationPickerShown = false;
				extraOptions.blockData = { aiSummaryShowTrend: false };
			}

			const report = reportSlide.report!;
			const popup = this.mmReportsService.openCreateBlockPopup({
				...extraOptions,
				report,
				onSuccess: (blockRecord) => {
					const blockRecordId = +blockRecord.id!;
					const block = this.mmBlocksService.findModel(String(blockRecordId))!;

					this.trackDatablockDrop(ReportSlideFragmentType.BLOCK, block);

					let fragment: ReportSlideFragment;
					if (placeholder) {
						fragment = placeholder;
						fragment.type = ReportSlideFragmentType.BLOCK;
						fragment.referenceId = blockRecordId;
					} else {
						let value: RecordData.ReportSlideFragment.Value | undefined;
						if (layout === NewReportSlideFragmentLayout.METRIC) {
							const randomColor = this.getRandomColor();
							value = {
								color: randomColor,
								themeId: this.availableThemesForColor(randomColor)[0] || 1,
							};
						} else if (layout === NewReportSlideFragmentLayout.AI_PERFORMANCE_SUMMARY) {
							const whiteColor = this.getColor('White');
							if (whiteColor) {
								value = {
									color: whiteColor,
									themeId: this.availableThemesForColor(whiteColor)[0] || 1,
								};
							}
						}

						fragment = this.createFragmentForSlide(
							reportSlide.numericId!,
							ReportSlideFragmentType.BLOCK,
							blockRecordId,
							value,
						);
					}

					resolve({ fragment });
				},
			});

			popup.on(PopupEvent.CLOSING, this, reject);
		});
	}

	private createDataStoryFragment(
		reportSlide: ReportSlide,
		placeholder?: ReportSlideFragment,
	): Promise<CreateFragmentResponseToGrid> {
		return this.mmDataSelectsService
			.openMetricSelectWithAttributes({
				requireSingleOutput: true,
			})
			.then((metricSetupData: DSSetup.MetricSetupData) => {
				let fragment: ReportSlideFragment;
				if (placeholder) {
					fragment = placeholder;
					fragment.type = ReportSlideFragmentType.STORY_TEXT;
				} else {
					fragment = this.createFragmentForSlide(
						reportSlide.numericId!,
						ReportSlideFragmentType.STORY_TEXT,
					);
				}

				const afterFragmentCreateFn = () => {
					// we do this after the fragment is saved bcos
					// the block that we need here is added by BE on fragment create
					const block = this.mmBlocksService.findModel(String(fragment.blockId!))!;

					const visualization = block.blockSetup.visualization as VisualizationDataStory;

					block.blockSetup.createPrimaryMainDataSelect({
						metricSetupKey: metricSetupData.key,
						metricSetupSpaceAccessId: metricSetupData.spaceAccessId,
						metricSetupDimensions: metricSetupData.dimensions,
						metricSetupFilters: metricSetupData.filters,
					});

					return block.blockSetup.afterSaves().then(() => {
						visualization.addContentAndDsMap();
						visualization.block.blockSetup.saveBlock();

						this.eventManagerService.trigger(AppEvent.DESIGNER_DATA_STORY_OVERRIDE, {
							block,
						});

						this.trackDatablockDrop(ReportSlideFragmentType.BLOCK, block);
					});
				};

				return { fragment, afterFragmentCreateFn };
			});
	}

	private createBoardFragment(
		reportSlide: ReportSlide,
		placeholder?: ReportSlideFragment,
	): Promise<CreateFragmentResponseToGrid> {
		return new Promise((resolve, reject) => {
			const popup = this.mmBoardsService.createBoardsSelectListModal();

			popup.open({
				model: this.mmBoardsService.normalBoardModels,
				maxSelectable: 1,
				isReport: true,
				onConfirm: async (selected: number[]) => {
					if (selected.length === 0) {
						reject();
						return Promise.resolve(true);
					}

					const boardId = selected[0]!;
					let fragment: ReportSlideFragment;

					if (placeholder) {
						fragment = placeholder;
						fragment.type = ReportSlideFragmentType.BOARD;
						fragment.referenceId = boardId;
					} else {
						fragment = this.createFragmentForSlide(
							reportSlide.numericId!,
							ReportSlideFragmentType.BOARD,
							boardId,
						);
					}

					this.trackDatablockDrop(ReportSlideFragmentType.BOARD);
					resolve({ fragment });
					return Promise.resolve(true);
				},
				onCancel: reject,
			});

			popup.on(PopupEvent.CLOSING, this, reject);
		});
	}

	private createTableFragment(
		reportSlide: ReportSlide,
		placeholder?: ReportSlideFragment,
	): Promise<CreateFragmentResponseToGrid> {
		let fragment: ReportSlideFragment;
		if (placeholder) {
			fragment = placeholder;
			fragment.type = ReportSlideFragmentType.STORY_TEXT;
		} else {
			fragment = this.createFragmentForSlide(
				reportSlide.numericId!,
				ReportSlideFragmentType.STORY_TEXT,
			);
		}

		const afterFragmentCreateFn = () => {
			const block = this.mmBlocksService.findModel(String(fragment.blockId!))!;

			// initialize content for the block
			// without this block is not found when event is triggered
			const visualization = block.blockSetup.visualization as VisualizationDataStory;
			visualization.addEmptyContent();
			block.blockSetup.saveBlock();

			return block.blockSetup.afterSaves().then(() => {
				this.eventManagerService.trigger(AppEvent.DATA_STORY_INSERT_TABLE, {
					block: block,
					tableOptions: {
						rows: 2,
						columns: 2,
					},
				});

				this.trackDatablockDrop(ReportSlideFragmentType.BLOCK, block, 'Text - table');
			});
		};

		return Promise.resolve({ fragment, afterFragmentCreateFn });
	}

	private createDividerFragment(
		reportSlide: ReportSlide,
		placeholder?: ReportSlideFragment,
	): Promise<CreateFragmentResponseToGrid> {
		let fragment: ReportSlideFragment;
		if (placeholder) {
			fragment = placeholder;
			fragment.type = ReportSlideFragmentType.STORY_TEXT;
		} else {
			fragment = this.createFragmentForSlide(
				reportSlide.numericId!,
				ReportSlideFragmentType.STORY_TEXT,
			);
		}

		const afterFragmentCreateFn = () => {
			const block = this.mmBlocksService.findModel(String(fragment.blockId!))!;

			// initialize content for the block
			// without this block is not found when event is triggered
			const visualization = block.blockSetup.visualization as VisualizationDataStory;
			visualization.addEmptyContent();
			block.blockSetup.saveBlock();

			return block.blockSetup.afterSaves().then(() => {
				this.eventManagerService.trigger(AppEvent.DATA_STORY_INSERT_DIVIDER, {
					block: block,
				});

				this.trackDatablockDrop(ReportSlideFragmentType.BLOCK, block, 'Text - divider');
			});
		};

		return Promise.resolve({ fragment, afterFragmentCreateFn });
	}

	getLimitationFromNewFragmentLayout(layout: NewReportSlideFragmentLayout) {
		switch (layout) {
			case NewReportSlideFragmentLayout.DATA_STORY:
				return BillingFeature.VISUALIZATIONS_ADVANCED;
			case NewReportSlideFragmentLayout.AI_PERFORMANCE_SUMMARY:
				return BillingFeature.GENERATIVE_AI;
			default:
				return null; // NewReportSlideFragmentLayout.TITLE, METRIC, TEXT, IMAGE
		}
	}

	trackDatablockDrop(
		fragmentType: ReportSlideFragmentType,
		block?: Block,
		customTrackingName?: string,
	) {
		let trackingName = 'Text';
		if (customTrackingName) {
			trackingName = customTrackingName;
		} else if (block) {
			trackingName = block.visualizationName;
		} else if (fragmentType === ReportSlideFragmentType.TITLE) {
			trackingName = 'Header';
		} else if (fragmentType === ReportSlideFragmentType.IMAGE) {
			trackingName = 'Image';
		} else if (fragmentType === ReportSlideFragmentType.BOARD) {
			trackingName = 'Databoard';
		}

		const metric = block?.blockSetup?.firstShownDataSelect?.metricSetup?.metric;

		const eventData: APIData.Tracking.Data = {
			page: 'reports',
			origin: 'metrickey_editor',
			visualization: trackingName,
		};

		if (metric) {
			eventData.metrics = [metric.name];
			eventData.source = metric.dataSourceTypeKey;
		}

		this.analyticsService.event(TrackingEvent.DATABLOCK_DROP, eventData);
	}

	// ------ For-store ------

	modelName = ModelName.REPORT_SLIDE_FRAGMENT;

	createModel(model: ReportSlideFragmentRecord): ReportSlideFragment {
		return new ReportSlideFragment({ model });
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/report-slide-fragments': ModelManagerReportSlideFragmentsService;
	}
}
